/* Veyz World theme behaviour.
   Motion is progressive: everything works without it, fine pointers get the
   richer layer (glow, tilt, magnetic buttons), touch gets calm equivalents,
   and prefers-reduced-motion switches the decorative motion off. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  // ── reveal on scroll ─────────────────────────────────────────────────────
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }), { rootMargin: '0px 0px -8% 0px' })
    : null;
  $$('.reveal').forEach((el) => (io ? io.observe(el) : el.classList.add('in')));

  // ── mobile menu ──────────────────────────────────────────────────────────
  const toggle = $('[data-menu-toggle]');
  toggle?.addEventListener('click', () => {
    const open = document.body.classList.toggle('menu-open');
    toggle.setAttribute('aria-expanded', open);
  });
  $$('.mobile-menu a').forEach((a) => a.addEventListener('click', () => document.body.classList.remove('menu-open')));

  // ── header: transparent over hero, condensed after, hides on scroll down ─
  const header = $('[data-header]');
  let lastY = scrollY;
  const onScroll = () => {
    const y = scrollY;
    header?.classList.toggle('scrolled', y > 40);
    if (!document.body.classList.contains('menu-open')) header?.classList.toggle('hide', y > lastY && y > innerHeight * 0.6);
    lastY = y;
  };
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // ── hero: pointer light + scroll parallax ────────────────────────────────
  const hero = $('[data-hero]');
  const heroInner = $('[data-hero-inner]');
  const pointer = { x: 0, y: 0 };
  if (hero) {
    if (finePointer && !reduced) {
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        hero.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        hero.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        pointer.x = (e.clientX / innerWidth - 0.5) * 2;
        pointer.y = (e.clientY / innerHeight - 0.5) * 2;
      });
    }
    if (!reduced) {
      addEventListener('scroll', () => {
        const k = Math.min(1, scrollY / innerHeight);
        if (heroInner) {
          heroInner.style.transform = `translate3d(${pointer.x * -6}px, ${scrollY * 0.28 + pointer.y * -4}px, 0)`;
          heroInner.style.opacity = String(1 - k * 1.15);
        }
      }, { passive: true });
    }
  }

  // ── hero starfield with depth: near stars drift faster and follow the pointer
  const canvas = $('[data-stars]');
  if (canvas && !reduced) {
    const ctx = canvas.getContext('2d');
    let w, h, stars, visible = true;
    const resize = () => {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round((w * h) / (finePointer ? 7000 : 11000));
      stars = Array.from({ length: count }, () => {
        const z = Math.random();
        return { x: Math.random() * w, y: Math.random() * h, z, r: 0.25 + z * 1.2, a: 0.12 + z * 0.6, t: Math.random() * 6 };
      });
    };
    resize();
    addEventListener('resize', resize);
    new IntersectionObserver(([e]) => (visible = e.isIntersecting)).observe(canvas);
    const px = { x: 0, y: 0 };
    const draw = (now) => {
      requestAnimationFrame(draw);
      if (!visible) return;
      px.x += (pointer.x - px.x) * 0.04;
      px.y += (pointer.y - px.y) * 0.04;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        s.y -= 0.03 + s.z * 0.14;
        if (s.y < -4) { s.y = h + 4; s.x = Math.random() * w; }
        const ox = px.x * s.z * -18, oy = px.y * s.z * -12;
        ctx.globalAlpha = s.a * (0.65 + 0.35 * Math.sin(now / 1500 + s.t));
        ctx.fillStyle = '#f2f0ea';
        ctx.beginPath();
        ctx.arc(s.x + ox, s.y + oy, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    requestAnimationFrame(draw);
  }

  // ── chapter index ────────────────────────────────────────────────────────
  const rail = $('[data-chapter-rail]');
  if (rail && 'IntersectionObserver' in window) {
    const links = $$('a', rail);
    const chapters = $$('[data-chapter]');
    const spy = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((a) => a.classList.toggle('active', a.dataset.rail === e.target.id));
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    chapters.forEach((c) => spy.observe(c));
    addEventListener('scroll', () => rail.classList.toggle('on', scrollY > innerHeight * 0.7), { passive: true });
  }

  // ── fine-pointer extras: card tilt + magnetic buttons ───────────────────
  if (finePointer && !reduced) {
    $$('[data-tilt]').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
        card.style.setProperty('--ry', `${(x - 0.5) * 7}deg`);
        card.style.setProperty('--rx', `${(0.5 - y) * 7}deg`);
        card.style.setProperty('--px', `${x * 100}%`);
        card.style.setProperty('--py', `${y * 100}%`);
      });
      card.addEventListener('pointerleave', () => { card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg'); });
    });
    $$('[data-magnetic]').forEach((btn) => {
      btn.addEventListener('pointermove', (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.setProperty('--bx', `${(e.clientX - r.left - r.width / 2) * 0.22}px`);
        btn.style.setProperty('--by', `${(e.clientY - r.top - r.height / 2) * 0.35}px`);
      });
      btn.addEventListener('pointerleave', () => { btn.style.setProperty('--bx', '0px'); btn.style.setProperty('--by', '0px'); });
    });
  }

  // ── live farm preview: isometric plant, packets flowing into the core ───
  const teaser = $('[data-farm-teaser]');
  if (teaser) {
    const ctx = teaser.getContext('2d');
    const xpEl = $('[data-teaser-xp]');
    let w, h, s, cx, cy, xp = 0, visible = false, flare = 0;
    const iso = (x, z, y = 0) => [cx + (x - z) * s * 0.87, cy + (x + z) * s * 0.5 - y * s];
    const nodes = {
      d1: [-3.2, -1.6], d2: [-3.4, 0], d3: [-3.2, 1.6],
      h1: [-1.6, -0.9], h2: [-1.6, 0.9], t: [-0.2, 0], c: [1.4, 0],
    };
    const routes = [['d1', 'h1', 't', 'c'], ['d2', 'h1', 't', 'c'], ['d3', 'h2', 't', 'c'], ['d2', 'h2', 't', 'c']];
    const packets = [];
    const resize = () => {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      w = teaser.clientWidth; h = teaser.clientHeight;
      teaser.width = w * dpr; teaser.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      s = Math.min(w, h * 1.3) / 8.4; cx = w * 0.52; cy = h * 0.56;
    };
    const box = (x, z, bw, bd, bh, edge) => {
      const p = (dx, dz, y) => iso(x + dx, z + dz, y);
      const top = [p(-bw, -bd, bh), p(bw, -bd, bh), p(bw, bd, bh), p(-bw, bd, bh)];
      const left = [p(-bw, bd, 0), p(bw, bd, 0), p(bw, bd, bh), p(-bw, bd, bh)];
      const right = [p(bw, -bd, 0), p(bw, bd, 0), p(bw, bd, bh), p(bw, -bd, bh)];
      for (const [face, fill] of [[left, '#14171c'], [right, '#0f1216'], [top, '#1c2129']]) {
        ctx.beginPath(); face.forEach(([fx, fy], i) => (i ? ctx.lineTo(fx, fy) : ctx.moveTo(fx, fy))); ctx.closePath();
        ctx.fillStyle = fill; ctx.fill();
        ctx.strokeStyle = edge; ctx.lineWidth = 1; ctx.stroke();
      }
    };
    const spawn = () => {
      const route = routes[(Math.random() * routes.length) | 0].map((k) => nodes[k]);
      packets.push({ route, seg: 0, t: 0, v: 0.9 + Math.random() * 0.5 });
    };
    let last = performance.now(), clock = 0;
    const frame = (now) => {
      requestAnimationFrame(frame);
      if (!visible) { last = now; return; }
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      clock += dt;
      if (clock > (reduced ? 9e9 : 0.55)) { clock = 0; spawn(); }
      flare = Math.max(0, flare - dt * 1.8);
      ctx.clearRect(0, 0, w, h);

      // ground grid
      ctx.lineWidth = 1;
      for (let i = -5; i <= 4; i++) {
        ctx.strokeStyle = 'rgba(167, 174, 183, 0.07)';
        let [ax, ay] = iso(i, -3.2), [bx, by] = iso(i, 3.2);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
        [ax, ay] = iso(-5, i * 0.75); [bx, by] = iso(4, i * 0.75);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      }
      // conduits
      ctx.strokeStyle = 'rgba(143, 207, 255, 0.22)';
      for (const r of routes) {
        ctx.beginPath();
        r.forEach((k, i) => { const [x, y] = iso(nodes[k][0], nodes[k][1], 0.05); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
        ctx.stroke();
      }
      // structures
      for (const k of ['d1', 'd2', 'd3']) box(nodes[k][0], nodes[k][1], 0.18, 0.18, 0.9, 'rgba(191,223,255,0.25)');
      for (const k of ['h1', 'h2']) box(nodes[k][0], nodes[k][1], 0.5, 0.36, 0.45, 'rgba(191,223,255,0.22)');
      box(nodes.t[0], nodes.t[1], 0.26, 0.26, 0.8, 'rgba(191,223,255,0.22)');
      // core column
      const [kx, ky] = iso(nodes.c[0], nodes.c[1], 0);
      const colH = s * 3.2;
      const g = ctx.createLinearGradient(0, ky - colH, 0, ky);
      g.addColorStop(0, 'rgba(191,223,255,0.05)'); g.addColorStop(1, `rgba(191,223,255,${0.55 + flare * 0.4})`);
      ctx.fillStyle = g; ctx.fillRect(kx - s * 0.09, ky - colH, s * 0.18, colH);
      ctx.save(); ctx.shadowColor = '#bfdfff'; ctx.shadowBlur = 24 + flare * 40;
      ctx.fillStyle = `rgba(232,244,255,${0.75 + flare * 0.25})`; ctx.fillRect(kx - 1.5, ky - colH * 0.95, 3, colH * 0.95);
      ctx.restore();
      ctx.strokeStyle = `rgba(191,223,255,${0.25 + flare * 0.5})`;
      ctx.beginPath(); ctx.ellipse(kx, ky, s * (0.6 + flare * 0.5), s * (0.3 + flare * 0.25), 0, 0, Math.PI * 2); ctx.stroke();

      // packets
      ctx.save(); ctx.shadowColor = '#bfdfff'; ctx.shadowBlur = 12; ctx.fillStyle = '#eef6ff';
      for (let i = packets.length - 1; i >= 0; i--) {
        const p = packets[i];
        const a = p.route[p.seg], b = p.route[p.seg + 1];
        p.t += dt * p.v * 1.4;
        if (p.t >= 1) {
          p.t = 0; p.seg++;
          if (p.seg >= p.route.length - 1) {
            packets.splice(i, 1); flare = 1; xp += 20 + ((Math.random() * 100) | 0);
            if (xpEl) xpEl.textContent = `+${xp.toLocaleString('en-US')} XP`;
            continue;
          }
          continue;
        }
        const [x, y] = iso(a[0] + (b[0] - a[0]) * p.t, a[1] + (b[1] - a[1]) * p.t, 0.08);
        ctx.beginPath(); ctx.arc(x, y, 2.2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    };
    resize();
    addEventListener('resize', resize);
    new IntersectionObserver(([e]) => (visible = e.isIntersecting)).observe(teaser);
    requestAnimationFrame(frame);
  }

  // ── add to cart (AJAX), header count updates ─────────────────────────────
  $$('.buy-form').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type=submit]');
      if (btn.dataset.added) { location.href = '/cart'; return; }
      btn.disabled = true;
      btn.textContent = btn.dataset.buyingLabel || 'Adding';
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!res.ok) throw new Error('add failed');
        const cart = await (await fetch('/cart.js')).json();
        $$('[data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
        btn.textContent = btn.dataset.addedLabel || 'Added · View cart';
        btn.dataset.added = '1';
        btn.disabled = false;
      } catch {
        form.submit();
      }
    });
  });

  // ── Studio 001 · what we build: tabs switch the showcase, auto-advancing until touched ──
  const layers = $('[data-layers]');
  if (layers) {
    const tabs = [...layers.querySelectorAll('[data-layer]')];
    const panes = [...layers.querySelectorAll('[data-pane]')];
    let cur = 0, timer = null, seen = false;
    const show = (n) => {
      cur = n;
      tabs.forEach((t, i) => { t.classList.toggle('on', i === n); t.setAttribute('aria-selected', i === n); });
      panes.forEach((p, i) => {
        p.classList.toggle('on', i === n);
        const v = p.querySelector('video');
        if (!v) return;
        if (i === n) { if (!v.src) v.src = v.dataset.src; v.play().catch(() => {}); } else v.pause();
      });
    };
    const auto = () => { clearInterval(timer); if (!reduced) timer = setInterval(() => seen && show((cur + 1) % tabs.length), 6000); };
    tabs.forEach((t, i) => t.addEventListener('click', () => { show(i); clearInterval(timer); layers.classList.add('held'); }));
    // Only rotate while the section is on screen.
    new IntersectionObserver(([e]) => { seen = e.isIntersecting; layers.classList.toggle('live', seen); }, { threshold: 0.35 }).observe(layers);
    auto();
  }

  // ── contact: ?topic=xp-farm preselects the XP Farm topic ──────────────────
  if (new URLSearchParams(location.search).get('topic') === 'xp-farm') {
    const sel = $('[data-topic]');
    if (sel) sel.value = 'XP Farm';
  }
})();
